import React, { useState, useMemo } from 'react';
import { CostCenterRecord } from '../../interfaces/types';
import {
  formatCurrency,
  formatPeriod,
  getSourceClassName,
} from '../../utils/utils';

interface CostCenterTableProps {
  costCenters: CostCenterRecord[];
  displayPeriod: string;
  onRowClick: (costCenter: CostCenterRecord) => void;
}

type SortColumn =
  | 'name'
  | 'source'
  | 'state'
  | 'users'
  | 'budget'
  | 'consumed'
  | 'utilization';
type SortDirection = 'asc' | 'desc' | null;

export const CostCenterTable: React.FC<CostCenterTableProps> = ({
  costCenters,
  displayPeriod,
  onRowClick,
}) => {
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Cycle through: asc -> desc -> null
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortColumn(null);
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedCostCenters = useMemo(() => {
    if (!sortColumn || !sortDirection) {
      return costCenters;
    }

    return [...costCenters].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case 'name':
          aValue = a.name?.toLowerCase() || '';
          bValue = b.name?.toLowerCase() || '';
          break;
        case 'source':
          aValue = a.source?.toLowerCase() || '';
          bValue = b.source?.toLowerCase() || '';
          break;
        case 'state':
          aValue = a.state?.toLowerCase() || '';
          bValue = b.state?.toLowerCase() || '';
          break;
        case 'users':
          aValue = a.resources?.length || 0;
          bValue = b.resources?.length || 0;
          break;
        case 'budget':
          aValue = a.budgetAmount || 0;
          bValue = b.budgetAmount || 0;
          break;
        case 'consumed':
          aValue = a.consumedAmount || 0;
          bValue = b.consumedAmount || 0;
          break;
        case 'utilization':
          const aBudget = a.budgetAmount || 0;
          const bBudget = b.budgetAmount || 0;
          aValue = aBudget > 0 ? (a.consumedAmount || 0) / aBudget : 0;
          bValue = bBudget > 0 ? (b.consumedAmount || 0) / bBudget : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [costCenters, sortColumn, sortDirection]);

  const getSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return ' ↕';
    }
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  return (
    <div className="cost-center-table-shell cost-center-overview-table-shell">
      <table className="cost-center-table cost-center-overview-table">
        <thead>
          <tr>
            <th
              onClick={() => handleSort('name')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
              title="Click to sort by Name"
            >
              Name{getSortIcon('name')}
            </th>
            <th
              onClick={() => handleSort('source')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
              title="Click to sort by Source"
            >
              Source{getSortIcon('source')}
            </th>
            <th
              onClick={() => handleSort('state')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
              title="Click to sort by State"
            >
              State{getSortIcon('state')}
            </th>
            <th
              onClick={() => handleSort('users')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
              title="Click to sort by Users"
            >
              Users{getSortIcon('users')}
            </th>
            <th>Period</th>
            <th
              onClick={() => handleSort('budget')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
              title="Click to sort by Budget"
            >
              Budget{getSortIcon('budget')}
            </th>
            <th
              onClick={() => handleSort('consumed')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
              title="Click to sort by Consumed"
            >
              Consumed{getSortIcon('consumed')}
            </th>
            <th
              onClick={() => handleSort('utilization')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
              title="Click to sort by Utilization"
            >
              Utilization{getSortIcon('utilization')}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedCostCenters.map((costCenter, index) => {
            const budgetAmount = costCenter.budgetAmount || 0;
            const consumedAmount = costCenter.consumedAmount || 0;
            const hasBudget = budgetAmount > 0;
            const utilization = hasBudget
              ? Math.min((consumedAmount / budgetAmount) * 100, 100)
              : 0;

            return (
              <tr
                key={costCenter.azureSubscription + index}
                className="cost-center-overview-row"
                tabIndex={0}
                role="button"
                onClick={() => onRowClick(costCenter)}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                  }
                }}
                aria-label={`Open details for ${costCenter.name}`}
              >
                <td>{costCenter.name}</td>
                <td>
                  <span
                    className={`cost-center-pill cost-center-pill--source ${getSourceClassName(
                      costCenter.source,
                    )}`}
                  >
                    {costCenter.source}
                  </span>
                </td>
                <td>
                  <span className="cost-center-pill cost-center-pill--status">
                    {costCenter.state}
                  </span>
                </td>
                <td>{costCenter.resources?.length || 0}</td>
                <td>{formatPeriod(displayPeriod)}</td>
                <td>{hasBudget ? formatCurrency(budgetAmount) : '—'}</td>
                <td className={hasBudget ? 'cost-center-consumed-value' : ''}>
                  {hasBudget ? formatCurrency(consumedAmount) : '—'}
                </td>
                <td>
                  {hasBudget ? (
                    <div className="cost-center-table-utilization">
                      <div
                        className="cost-center-table-utilization__bar"
                        aria-hidden="true"
                      >
                        <span
                          style={{
                            width: `${utilization}%`,
                          }}
                        />
                      </div>
                      <span>{utilization.toFixed(1)}%</span>
                    </div>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
